import { useEffect, useState } from 'react';

/**
 * $BIGW wallet helpers.
 *
 * Two jobs, both aimed at the email/social (Privy embedded wallet) user who
 * can't open MetaMask to inspect their holdings:
 *
 *  - `useBigwBalance` reads their $BIGW balance straight off BNB Chain over
 *    public RPC. Privy's balance API doesn't cover arbitrary BEP-20 tokens,
 *    so we call `balanceOf` ourselves — no API key, no backend.
 *  - `walletPortalUrl` points at Privy's hosted wallet portal (balance, add
 *    funds, export keys, activity), deep-linked to our app.
 */

/** $BIGW: 18-decimal BEP-20 token on BNB Chain (chainId 56). */
const BIGW_TOKEN = '0x66d6d7c20ec39e390ffd232818147957f2985ed3';
const BSC_RPC = 'https://bsc-rpc.publicnode.com';
const BALANCE_OF_SELECTOR = '0x70a08231'; // balanceOf(address)

let portalAppId: string | null = null;

/** Stashed at boot from /config so the portal link targets the right (dev vs
 *  prod) Privy app. Falls back to the portal root if never set. */
export function setWalletPortalAppId(appId: string): void {
  portalAppId = appId;
}

/** Privy's hosted wallet portal. The user re-authenticates there with the
 *  same login, then lands on their BigWater wallet. */
export function walletPortalUrl(): string {
  return portalAppId ? `https://home.privy.io/apps/${portalAppId}` : 'https://home.privy.io';
}

/** Format an 18-decimal raw balance to a compact string: thousands
 *  separators, at most 4 fractional digits, trailing zeros trimmed. */
export function formatBigw(raw: bigint): string {
  const base = 10n ** 18n;
  const whole = (raw / base).toLocaleString('en-US');
  const frac = (raw % base).toString().padStart(18, '0').slice(0, 4).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}

/** Read a wallet's $BIGW balance via eth_call. Returns null on any failure so
 *  callers can hide the row rather than render a broken value. */
export async function fetchBigwBalance(address: string): Promise<string | null> {
  const data = BALANCE_OF_SELECTOR + address.toLowerCase().replace(/^0x/, '').padStart(64, '0');
  try {
    const res = await fetch(BSC_RPC, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'eth_call',
        params: [{ to: BIGW_TOKEN, data }, 'latest'],
      }),
    });
    const json = (await res.json()) as { result?: string; error?: unknown };
    if (json.error || typeof json.result !== 'string') return null;
    return formatBigw(BigInt(json.result));
  } catch {
    return null;
  }
}

/** Lazily fetch the $BIGW balance for `address`, gated by `enabled` so the RPC
 *  call only fires when the surface showing it is actually open. */
export function useBigwBalance(
  address: string | null | undefined,
  enabled = true,
): { balance: string | null; loading: boolean } {
  const [balance, setBalance] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !address) return;
    let alive = true;
    setLoading(true);
    void fetchBigwBalance(address).then((b) => {
      if (!alive) return;
      setBalance(b);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [address, enabled]);

  return { balance, loading };
}
